/**
 * Orchestrator hook for async Select — composes:
 * - useSelectCommonBehavior: open, dismiss, keyboard (shared with sync)
 * - usePaginatedFetch: requests, cursors, loading, scroll, lifecycle
 * - useValueResolution: fullValue tracking, label resolution
 * - useDebouncedSearch: debounced search requests
 */
import { useCallback } from 'react';

import { useSelectCommonBehavior } from '../useSelectCommonBehavior';
import type { UseAsyncOptionsParams, UseAsyncOptionsReturn } from './types';
import { useDebouncedSearch } from './useDebouncedSearch';
import { usePaginatedFetch } from './usePaginatedFetch';
import { useValueResolution } from './useValueResolution';

export const useAsyncOptions = <
  isNullable extends boolean = false,
  T = undefined,
>({
  loadOptions,
  value,
  disabled,
  onBlur,
  onOpenChange: passedOnOpenChange,
  onChange: passedOnChange,
}: UseAsyncOptionsParams<isNullable, T>): UseAsyncOptionsReturn<
  isNullable,
  T
> => {
  const { open, setOpen, handleDismiss, handleTriggerKeyDown } =
    useSelectCommonBehavior({
      disabled,
      onBlur,
    });

  const {
    options,
    setOptions,
    loading,
    inlineLoading,
    isFirstLoad,
    makeRequest,
    onOpenChange: paginatedOnOpenChange,
    onScroll,
  } = usePaginatedFetch<T>({
    loadOptions,
    onOpenChange: passedOnOpenChange,
  });

  const { handleChange, optionsWithSelected } = useValueResolution<
    isNullable,
    T
  >({
    loadOptions,
    value,
    options,
    setOptions,
    onChange: passedOnChange,
  });

  const onSearchChange = useDebouncedSearch<T>({
    loadOptions,
    isFirstLoad,
    makeRequest,
  });

  // Wrap paginatedOnOpenChange to pass the current value for initial load
  const onOpenChange = useCallback(
    (isOpen: boolean) => {
      paginatedOnOpenChange(isOpen, value);
    },
    [paginatedOnOpenChange, value],
  );

  return {
    open,
    setOpen,
    options: optionsWithSelected,
    loading,
    inlineLoading,
    handleChange,
    handleDismiss,
    onSearchChange,
    onScroll,
    onOpenChange,
    handleTriggerKeyDown,
  };
};
